import { useEffect, useState } from "react";
import { api, errText } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { ItemVisual, PriceTag } from "./storeui";
import { Plus, Trash2, Star, Eye, EyeOff, Pencil, X, Sparkles, Wand2, Loader2, Film } from "lucide-react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const CAT_BY_KIND = { background: "backgrounds", animation: "animations", badge: "badges", frame: "frames" };

function AIStudio({ onSaved, cats }) {
  const [kind, setKind] = useState("background");
  const [prompt, setPrompt] = useState("");
  const [refs, setRefs] = useState([]); // base64 strings
  const [busy, setBusy] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [result, setResult] = useState(null); // {url, kind, animUrl}
  const [name, setName] = useState("");
  const [price, setPrice] = useState(0);

  const addRefs = (files) => {
    Array.from(files || []).slice(0, 3).forEach((f) => {
      const r = new FileReader();
      r.onload = () => setRefs((x) => [...x, String(r.result).split(",").pop()]);
      r.readAsDataURL(f);
    });
  };

  const generate = async () => {
    if (!prompt.trim()) { toast.error("Describe what to create"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post("/admin/store/ai/generate", { prompt, kind, reference_images: refs }, { timeout: 120000 });
      setResult({ url: data.url, kind });
    } catch (e) { toast.error(errText(e)); } finally { setBusy(false); }
  };

  const animate = async () => {
    if (!result?.url) return;
    setAnimating(true);
    try {
      const { data } = await api.post("/admin/store/ai/animate", { image_url: `${BACKEND}${result.url}`, prompt }, { timeout: 600000 });
      setResult((r) => ({ ...r, animUrl: data.url }));
      toast.success("Animation ready");
    } catch (e) { toast.error(errText(e)); } finally { setAnimating(false); }
  };

  const saveProduct = async () => {
    if (!result?.url || !name.trim()) { toast.error("Add a product name"); return; }
    const abs = `${BACKEND}${result.animUrl || result.url}`;
    let config = {};
    if (kind === "background") config = { type: "image", value: `url('${BACKEND}${result.url}') center/cover`, text: "light" };
    else if (kind === "badge") config = { label: name.slice(0, 16) };
    const cat = (cats || []).find((c) => c.kind === kind)?.slug || CAT_BY_KIND[kind];
    const body = { name, description: prompt.slice(0, 160), kind, category: cat, price: Number(price) || 0,
      config, preview_url: abs, published: true, image_url: kind === "badge" ? `${BACKEND}${result.url}` : undefined };
    try {
      await api.post("/admin/store/items", body);
      toast.success("Product created in Store");
      setResult(null); setName(""); setPrompt(""); setRefs([]);
      onSaved && onSaved();
    } catch (e) { toast.error(errText(e)); }
  };

  return (
    <div className="space-y-4" data-testid="ai-studio">
      <div className="surface rounded-2xl p-4 flex items-center gap-2 text-sm text-muted-foreground">
        <Sparkles size={16} className="text-gold shrink-0" />Generate brand-new profile products with AI. Upload reference images, describe it, generate, then save it straight to the Store.
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-3">
          <div>
            <label className="text-xs text-muted-foreground">Product type</label>
            <select value={kind} onChange={(e) => setKind(e.target.value)} className="w-full surface-2 border border-white/10 rounded-md h-10 px-2 text-sm" data-testid="ai-kind">
              {KINDS.map((k) => <option key={k} value={k} className="capitalize">{k}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Reference images (optional, up to 3)</label>
            <input type="file" accept="image/*" multiple onChange={(e) => addRefs(e.target.files)} className="block w-full text-xs mt-1" data-testid="ai-refs" />
            {refs.length > 0 && <div className="flex gap-1.5 mt-2">{refs.map((r, i) => <img key={i} src={`data:image/png;base64,${r}`} alt="" className="w-12 h-12 rounded object-cover" />)}</div>}
          </div>
          <div>
            <label className="text-xs text-muted-foreground">Prompt</label>
            <Textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="e.g. Luxury black marble with gold veins and soft bokeh" className="surface-2 border-white/10 min-h-[90px]" data-testid="ai-prompt" />
          </div>
          <Button onClick={generate} disabled={busy} className="btn-gold rounded-xl w-full h-11" data-testid="ai-generate-btn">
            {busy ? <Loader2 size={16} className="animate-spin mr-1.5" /> : <Wand2 size={16} className="mr-1.5" />}{busy ? "Generating…" : "Generate"}
          </Button>
        </div>
        <div className="space-y-3">
          <div className="surface-2 rounded-xl aspect-video flex items-center justify-center overflow-hidden border border-white/10" data-testid="ai-preview">
            {result?.animUrl ? <video src={`${BACKEND}${result.animUrl}`} autoPlay loop muted playsInline className="w-full h-full object-cover" />
              : result?.url ? <img src={`${BACKEND}${result.url}`} alt="" className="w-full h-full object-cover" />
              : <span className="text-xs text-muted-foreground">Preview appears here</span>}
          </div>
          {result?.url && (
            <>
              {kind === "animation" && !result.animUrl && (
                <Button onClick={animate} disabled={animating} variant="outline" className="rounded-xl w-full border-white/15" data-testid="ai-animate-btn">
                  {animating ? <Loader2 size={15} className="animate-spin mr-1.5" /> : <Film size={15} className="mr-1.5" />}{animating ? "Animating… (up to a few min)" : "Animate with fal.ai"}
                </Button>
              )}
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Product name" className="surface-2 border-white/10" data-testid="ai-name" />
              <Input type="number" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="Price (VC)" className="surface-2 border-white/10" data-testid="ai-price" />
              <Button onClick={saveProduct} className="btn-blue rounded-xl w-full h-11" data-testid="ai-save-btn"><Plus size={16} className="mr-1.5" />Save as Store product</Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

const KINDS = ["animation", "background", "badge", "frame"];
const CONFIG_HINTS = {
  background: '{"type":"gradient","value":"linear-gradient(180deg,#0c0c0e,#060607)","text":"light"}',
  animation: '{"effect":"glow","target":"avatar"}  // effect: glow|float|pulse|shimmer, target: avatar|name',
  frame: '{"ring":"conic-gradient(from 180deg,#d4af37,#f9e08a,#d4af37)","glow":"0 0 18px rgba(212,175,55,.6)"}',
  badge: '{"label":"VIP","color":"#d4af37","icon":"crown"}  // icon: crown|flame|sparkles|shirt|star|gem',
};

function emptyItem() {
  return { name: "", description: "", kind: "background", category: "backgrounds", price: 0, config: "", published: true, featured: false, stock: "", vstar_boost: 0 };
}

export function StoreAdmin() {
  const [sub, setSub] = useState("items");
  const [items, setItems] = useState(null);
  const [cats, setCats] = useState(null);
  const [orders, setOrders] = useState(null);
  const [editing, setEditing] = useState(null); // item form
  const [newCat, setNewCat] = useState({ name: "", kind: "background", order: 0 });

  const loadItems = () => api.get("/admin/store/items").then(({ data }) => setItems(data));
  const loadCats = () => api.get("/admin/store/categories").then(({ data }) => setCats(data));
  const loadOrders = () => api.get("/admin/store/orders").then(({ data }) => setOrders(data));

  useEffect(() => {
    if (sub === "items") { if (!items) loadItems(); if (!cats) loadCats(); }
    if (sub === "ai" && !cats) loadCats();
    if (sub === "categories" && !cats) loadCats();
    if (sub === "orders" && !orders) loadOrders();
  }, [sub]); // eslint-disable-line

  const saveItem = async () => {
    const f = editing;
    if (!f.name || !f.category) { toast.error("Name and category required"); return; }
    let config = null;
    if (f.config?.trim()) { try { config = JSON.parse(f.config); } catch { toast.error("Config must be valid JSON"); return; } }
    const body = { name: f.name, description: f.description, kind: f.kind, category: f.category,
      price: Number(f.price) || 0, config, published: !!f.published, featured: !!f.featured,
      stock: f.stock === "" || f.stock === null ? null : Number(f.stock),
      vstar_boost: f.kind === "badge" ? (Number(f.vstar_boost) || 0) : 0 };
    try {
      if (f.id) await api.put(`/admin/store/items/${f.id}`, body);
      else await api.post("/admin/store/items", body);
      toast.success("Item saved"); setEditing(null); loadItems();
    } catch (e) { toast.error(errText(e)); }
  };

  const editItem = (it) => setEditing({ ...it, config: it.config ? JSON.stringify(it.config, null, 2) : "", stock: it.stock ?? "" });

  const toggle = async (it, field) => {
    try { await api.post(`/admin/store/items/${it.id}/${field}?${field === "publish" ? "published" : "featured"}=${field === "publish" ? !it.published : !it.featured}`); loadItems(); }
    catch (e) { toast.error(errText(e)); }
  };
  const delItem = async (it) => {
    if (!window.confirm(`Remove "${it.name}"? Owners keep it; item is disabled if owned.`)) return;
    try { const { data } = await api.delete(`/admin/store/items/${it.id}`); toast.success(data.disabled ? "Disabled (owned by users)" : "Deleted"); loadItems(); }
    catch (e) { toast.error(errText(e)); }
  };
  const addCat = async () => {
    if (!newCat.name) return;
    try { await api.post("/admin/store/categories", newCat); toast.success("Category added"); setNewCat({ name: "", kind: "background", order: 0 }); loadCats(); }
    catch (e) { toast.error(errText(e)); }
  };
  const delCat = async (c) => { try { await api.delete(`/admin/store/categories/${c.id}`); loadCats(); } catch (e) { toast.error(errText(e)); } };
  const refund = async (o) => { if (!window.confirm("Refund this order?")) return; try { await api.post(`/admin/store/orders/${o.id}/refund`); toast.success("Refunded"); loadOrders(); } catch (e) { toast.error(errText(e)); } };

  return (
    <div data-testid="store-admin">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <Tabs value={sub} onValueChange={setSub}>
          <TabsList className="surface-2">
            {["items", "categories", "orders", "ai"].map((t) => <TabsTrigger key={t} value={t} data-testid={`store-admin-tab-${t}`} className="capitalize data-[state=active]:bg-white/10 data-[state=active]:text-gold">{t === "ai" ? "AI Studio" : t}</TabsTrigger>)}
          </TabsList>
        </Tabs>
        {sub === "items" && <Button onClick={() => setEditing(emptyItem())} className="btn-blue rounded-full" data-testid="store-admin-new-item"><Plus size={15} className="mr-1" />New Item</Button>}
      </div>

      {sub === "ai" && <AIStudio onSaved={() => { setSub("items"); loadItems(); }} cats={cats} />}

      {sub === "items" && (
        !items ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div className="grid md:grid-cols-2 gap-3">
            {items.map((it) => (
              <div key={it.id} className="surface rounded-xl p-3 flex gap-3" data-testid={`store-admin-item-${it.id}`}>
                <div className="w-24 shrink-0"><ItemVisual item={it} size="h-20" /></div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 text-sm font-semibold truncate">{it.name}{it.featured && <Star size={12} className="text-gold" />}{!it.published && <span className="text-[10px] text-muted-foreground">(hidden)</span>}{it.disabled && <span className="text-[10px] text-rose-400">(disabled)</span>}</div>
                  <div className="text-[11px] text-muted-foreground capitalize">{it.kind} · {it.category} · {it.sold_count || 0} sold</div>
                  <div className="mt-1"><PriceTag price={it.price} /></div>
                  <div className="flex gap-1 mt-2">
                    <button onClick={() => editItem(it)} className="px-2 py-1 rounded surface-2 text-xs hover:text-eblue" data-testid={`store-admin-edit-${it.id}`}><Pencil size={13} /></button>
                    <button onClick={() => toggle(it, "publish")} className="px-2 py-1 rounded surface-2 text-xs hover:text-eblue">{it.published ? <EyeOff size={13} /> : <Eye size={13} />}</button>
                    <button onClick={() => toggle(it, "feature")} className={`px-2 py-1 rounded surface-2 text-xs ${it.featured ? "text-gold" : "hover:text-gold"}`}><Star size={13} /></button>
                    <button onClick={() => delItem(it)} className="px-2 py-1 rounded surface-2 text-xs hover:text-rose-400 ml-auto"><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
      )}

      {sub === "categories" && (
        <div className="max-w-lg space-y-3">
          <div className="surface rounded-xl p-3 flex gap-2 items-end flex-wrap">
            <div className="flex-1 min-w-[120px]"><label className="text-xs text-muted-foreground">Name</label><Input value={newCat.name} onChange={(e) => setNewCat({ ...newCat, name: e.target.value })} className="surface-2 border-white/10" data-testid="store-admin-cat-name" /></div>
            <div><label className="text-xs text-muted-foreground">Kind</label><select value={newCat.kind} onChange={(e) => setNewCat({ ...newCat, kind: e.target.value })} className="surface-2 border border-white/10 rounded-md px-2 py-2 text-sm block">{KINDS.map((k) => <option key={k}>{k}</option>)}</select></div>
            <Button onClick={addCat} className="btn-blue rounded-full" data-testid="store-admin-add-cat"><Plus size={15} /></Button>
          </div>
          {(cats || []).map((c) => (
            <div key={c.id} className="surface rounded-xl p-3 flex items-center justify-between" data-testid={`store-admin-cat-${c.id}`}>
              <div><div className="text-sm font-medium">{c.name}</div><div className="text-[11px] text-muted-foreground capitalize">{c.kind} · {c.slug}</div></div>
              <button onClick={() => delCat(c)} className="text-muted-foreground hover:text-rose-400"><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      )}

      {sub === "orders" && (
        !orders ? <p className="text-sm text-muted-foreground">Loading…</p> : (
          <div>
            <div className="surface rounded-xl p-4 mb-3 flex gap-6"><div><div className="text-xs text-muted-foreground">Orders</div><div className="font-display font-bold text-xl">{orders.count}</div></div><div><div className="text-xs text-muted-foreground">Store revenue</div><div className="font-display font-bold text-xl text-gold">{orders.total_revenue_vc?.toLocaleString()} VC</div></div></div>
            <div className="space-y-2">
              {orders.orders.map((o) => (
                <div key={o.id} className="surface rounded-xl p-3 flex items-center justify-between text-sm" data-testid={`store-admin-order-${o.id}`}>
                  <div><div className="font-medium">{o.item_name}</div><div className="text-[11px] text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.amount} VC {o.refunded && <span className="text-rose-400">· refunded</span>}</div></div>
                  {!o.refunded && o.amount > 0 && <button onClick={() => refund(o)} className="text-xs px-3 py-1.5 rounded-full surface-2 hover:text-rose-400" data-testid={`store-admin-refund-${o.id}`}>Refund</button>}
                </div>
              ))}
            </div>
          </div>
        )
      )}

      {/* Item editor */}
      {editing && (
        <div className="fixed inset-0 z-[80] bg-black/70 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="glass border border-white/10 rounded-2xl w-full max-w-md max-h-[88vh] overflow-y-auto p-5" onClick={(e) => e.stopPropagation()} data-testid="store-admin-item-form">
            <div className="flex items-center justify-between mb-3"><h3 className="font-display font-bold">{editing.id ? "Edit item" : "New item"}</h3><button onClick={() => setEditing(null)}><X size={18} /></button></div>
            <div className="space-y-2.5">
              <Input placeholder="Name" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="surface-2 border-white/10" data-testid="store-admin-form-name" />
              <Textarea placeholder="Description" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} className="surface-2 border-white/10" />
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-muted-foreground">Kind</label><select value={editing.kind} onChange={(e) => setEditing({ ...editing, kind: e.target.value })} className="w-full surface-2 border border-white/10 rounded-md px-2 py-2 text-sm">{KINDS.map((k) => <option key={k}>{k}</option>)}</select></div>
                <div><label className="text-xs text-muted-foreground">Category slug</label><select value={editing.category} onChange={(e) => setEditing({ ...editing, category: e.target.value })} className="w-full surface-2 border border-white/10 rounded-md px-2 py-2 text-sm" data-testid="store-admin-form-category">{(cats || []).map((c) => <option key={c.id} value={c.slug}>{c.name}</option>)}</select></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className="text-xs text-muted-foreground">Price (VC)</label><Input type="number" value={editing.price} onChange={(e) => setEditing({ ...editing, price: e.target.value })} className="surface-2 border-white/10" data-testid="store-admin-form-price" /></div>
                <div><label className="text-xs text-muted-foreground">Stock (blank = ∞)</label><Input type="number" value={editing.stock} onChange={(e) => setEditing({ ...editing, stock: e.target.value })} className="surface-2 border-white/10" /></div>
              </div>
              {editing.kind === "badge" && (
                <div><label className="text-xs text-muted-foreground">VStar score boost (badge)</label><Input type="number" value={editing.vstar_boost ?? 0} onChange={(e) => setEditing({ ...editing, vstar_boost: e.target.value })} className="surface-2 border-white/10" placeholder="e.g. 50" data-testid="store-admin-form-boost" /><p className="text-[10px] text-muted-foreground mt-1">Added to a buyer's VStar score when they purchase this badge.</p></div>
              )}
              <div><label className="text-xs text-muted-foreground">Config JSON</label><Textarea value={editing.config} onChange={(e) => setEditing({ ...editing, config: e.target.value })} className="surface-2 border-white/10 font-mono text-xs min-h-[90px]" placeholder={CONFIG_HINTS[editing.kind]} data-testid="store-admin-form-config" /><p className="text-[10px] text-muted-foreground mt-1 break-all">{CONFIG_HINTS[editing.kind]}</p></div>
              <div className="flex gap-4 text-sm">
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!editing.published} onChange={(e) => setEditing({ ...editing, published: e.target.checked })} />Published</label>
                <label className="flex items-center gap-1.5"><input type="checkbox" checked={!!editing.featured} onChange={(e) => setEditing({ ...editing, featured: e.target.checked })} />Featured</label>
              </div>
              <Button onClick={saveItem} className="btn-blue rounded-full w-full" data-testid="store-admin-save-item">Save item</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
